import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { ROUTES } from '@/app/router/routes';
import { analyticsService } from '@/services/analytics/analyticsService';
import { showToast } from '@/utils/globalToast';

/**
 * useResetPassword — handles the Firebase password reset link.
 *
 * Mirrors Flutter WebLoginBloc + the ResetPasswordConsumer listener:
 *   _OnValidateResetPwdCode → verifyPasswordResetCode (the form shows meanwhile, under a spinner)
 *   _OnClickResetPwdBtn → confirmPasswordReset
 *
 * Every outcome except a rejected password ends on /home with a toast:
 *   - link expired / invalid (on load): WebResetPwd.resetPwdCodeExpired
 *   - password updated: WebResetPwd.resetPwdSuccessMsg
 *   - update failed: WebResetPwd.resetPwdSuccessMsgFailed
 * A password Firebase rejects ("password-does-not-meet-requirements") shows Firebase's message
 * in the error-and-support dialog and stays on the form.
 *
 * Flutter source files:
 *   - application/web_auth/web_login/web_login_bloc.dart
 *   - infrastructure/auth/i_auth_repository.dart (validatePasswordResetCode, resetPasswordGoogleApi)
 *   - presentation/web/auth/reset_password/reset_password.dart
 */

export interface ResetPasswordState {
  isLoading: boolean;
  /** Firebase's message for a rejected password — shown in the support dialog */
  warningMessage: string;

  handleResetPassword: (newPassword: string) => Promise<void>;
  clearWarning: () => void;
}

export function useResetPassword(): ResetPasswordState {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const oobCode = searchParams.get('oobCode') ?? '';
  const tenantId = searchParams.get('tenantId') ?? '';

  const [isLoading, setIsLoading] = useState(true);
  const [warningMessage, setWarningMessage] = useState('');

  const clearWarning = useCallback(() => setWarningMessage(''), []);

  /** Flutter navigateTo(homeRoute, isClearStack: true) after the listener's toast. */
  const goHome = useCallback((message: string, type: 'success' | 'error') => {
    showToast(message, type);
    void analyticsService.logScreenView({ screenName: ROUTES.HOME });
    navigate(ROUTES.HOME, { replace: true });
  }, [navigate]);

  /**
   * Validate the reset code on mount.
   * Mirrors Flutter WebLoginBloc._OnValidateResetPwdCode.
   */
  useEffect(() => {
    let cancelled = false;
    if (tenantId) {
      auth.tenantId = tenantId;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        if (cancelled) return;
        if (email) setIsLoading(false);
        else goHome('Try verifying your reset password again\nYour request to verify your reset password has expired or the link has already been used', 'error');
      })
      .catch(() => {
        if (!cancelled) {
          goHome('Try verifying your reset password again\nYour request to verify your reset password has expired or the link has already been used', 'error');
        }
      });

    return () => { cancelled = true; };
  }, [oobCode, tenantId, goHome]);

  /**
   * Confirm the new password.
   * Mirrors Flutter WebLoginBloc._OnClickResetPwdBtn → IAuthRepository.resetPasswordGoogleApi.
   */
  const handleResetPassword = useCallback(async (newPassword: string) => {
    if (isLoading) return;

    setIsLoading(true);
    setWarningMessage('');

    try {
      if (tenantId) {
        auth.tenantId = tenantId;
      }
      await confirmPasswordReset(auth, oobCode, newPassword);
      goHome('Your reset password is updated successfully', 'success');
    } catch (err: unknown) {
      const firebaseError = err as { code?: string; message?: string };
      if ((firebaseError.code ?? '').includes('password-does-not-meet-requirements')) {
        setWarningMessage(firebaseError.message ?? '');
        setIsLoading(false);
      } else {
        goHome('Your reset password update is Failed\nTry verifying your reset password again', 'error');
      }
    }
  }, [oobCode, tenantId, isLoading, goHome]);

  return {
    isLoading,
    warningMessage,
    handleResetPassword,
    clearWarning,
  };
}
